'use client';

import { useEffect, type RefObject } from 'react';

/**
 * Standard dialog a11y plumbing for any custom modal that doesn't go through
 * Radix/shadcn's Dialog primitive.
 *
 * Usage:
 *   const dialogRef = useRef<HTMLDivElement>(null);
 *   const primaryBtnRef = useRef<HTMLButtonElement>(null);
 *   useModalA11y(isOpen, {
 *     dialogRef,
 *     primaryRef: primaryBtnRef,
 *     onDismiss: () => setOpen(false),
 *   });
 *
 * Handles, while open:
 * - Snapshots the previously-focused element and restores it on close.
 * - Focuses the primary CTA (or first focusable in the dialog if no primaryRef
 *   is supplied) after a 50ms tick — gives motion-driven mounts time to paint
 *   so the focus call doesn't race with the transition.
 * - Esc dismisses (calls onDismiss).
 * - Tab / Shift+Tab cycles within the dialog's focusable elements; focus can
 *   never leak out into background page controls.
 * - Body scroll lock so the page behind the modal doesn't scroll.
 *
 * Stays a no-op when isOpen is false. Safe to call unconditionally with a
 * stable ref pair.
 */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useModalA11y(
  isOpen: boolean,
  options: {
    dialogRef: RefObject<HTMLElement | null>;
    primaryRef?: RefObject<HTMLElement | null>;
    onDismiss: () => void;
  },
) {
  const { dialogRef, primaryRef, onDismiss } = options;

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = (typeof document !== 'undefined' ? document.activeElement : null) as
      | HTMLElement
      | null;

    const focusTimer = window.setTimeout(() => {
      const target =
        primaryRef?.current ??
        dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
        null;
      target?.focus();
    }, 50);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onDismiss();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [isOpen, dialogRef, primaryRef, onDismiss]);
}
